import React, { useState, useEffect } from 'react';
import { Table, Card, Btn, Modal, Input, Select, Textarea, FormGrid, SectionHeader, Badge, ProgressBar, StatCard, NeedProject, RNLLoader } from '../components/UI';
import { fmtNum, fmtDate, statusColor, exportCSV } from '../utils';
import * as api from '../api';
import { useParams, useNavigate } from 'react-router-dom';

// ─── PROJECT_DIVISIONS TAB ───────────────────────────
function ProjectDivisionsTab({ projectId, toast }) {
  const navigate = useNavigate();
  const [rows, setRows]         = useState([]);
  const [disciplines, setDiscs] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState({});
  const [saving, setSaving]     = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, d] = await Promise.all([api.listProjectDivisions(projectId), api.listDisciplines()]);
      setRows(Array.isArray(b) ? b : []);
      setDiscs(Array.isArray(d) ? d : []);
    } catch {}
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId]);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.project_division_id) await api.updateProjectDivision(form.project_division_id, form);
      else                  await api.createProjectDivision(projectId, form);
      toast.show('Project Division saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete project division?')) return;
    try { await api.deleteProjectDivision(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  const discOpts = [{ value: '', label: '— None —' }, ...disciplines.map(d => ({ value: d.id, label: d.name }))];
  const statusOpts = ['planning', 'in_progress', 'completed', 'on_hold', 'cancelled'].map(s => ({ value: s, label: s }));

  return (
    <>
      {loading ? (
        <RNLLoader title="Loading Project Divisions..." subtitle="Please wait" />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
          <StatCard label="Total Divisions" value={rows.length} color="cyan" icon="🏢" />
          <StatCard label="Active Divisions" value={rows.filter(r => r.status === 'active').length} color="amber" icon="⚡" />
          <StatCard label="Completed Divisions" value={rows.filter(r => r.status === 'completed').length} color="green" icon="✓" />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 14 }}>
        <Btn variant="ghost" onClick={() => exportCSV(rows, 'projectDivisions')}>⬇ CSV</Btn>
        <Btn onClick={() => { setForm({ status: 'planning' }); setModal(true); }}>Add Project Division</Btn>
      </div>
      <Card>
        <Table loading={loading} cols={[
          { key: 'name',              label: 'Name',      render: v => <strong>{v}</strong> },
          { key: 'code',              label: 'Code',      render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{v}</span> },
          { key: 'status',            label: 'Status',    render: v => <Badge color={statusColor(v)}>{v}</Badge> },
          { key: 'floor_count',       label: 'Floors' },
          { key: 'gross_floor_area',  label: 'GFA (m²)', render: v => v ? fmtNum(v) : '—' },
          { key: 'work_package',      label: 'Package',  render: v => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v || '—'}</span> },
          { key: 'planned_start',     label: 'Start',    render: v => fmtDate(v) },
          { key: 'planned_finish',    label: 'Finish',   render: v => fmtDate(v) },
          { key: 'project_division_milestones', label: 'Milestones', render: v => v?.length || 0 },
          { key: 'project_division_id',       label: '',         render: (id, row) => (
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn variant="secondary" size="sm" style={{ padding: '3px 8px' }} onClick={() => navigate(`/projects/${projectId}/project-divisions/${id}`)}>View</Btn>
              <Btn variant="ghost"  size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>
              <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>
            </div>
          )},
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title={form.project_division_id ? 'Edit ProjectDivision' : 'Add ProjectDivision'} onClose={() => setModal(false)} width={680}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input label="Name"  required value={form.name || ''} onChange={set('name')} />
              <Input label="Code"  required value={form.code || ''} onChange={set('code')} />
              <Input label="Floor Count"    type="number" value={form.floor_count || ''} onChange={set('floor_count')} />
              <Input label="GFA (m²)"       type="number" value={form.gross_floor_area || ''} onChange={set('gross_floor_area')} />
              <Input label="Planned Start"  type="date"   value={form.planned_start || ''} onChange={set('planned_start')} />
              <Input label="Planned Finish" type="date"   value={form.planned_finish || ''} onChange={set('planned_finish')} />
              <Select label="Status"     value={form.status || 'planning'} onChange={set('status')} options={statusOpts} />
              <Select label="Discipline" value={form.discipline_id || ''} onChange={set('discipline_id')} options={discOpts} />
              <Input label="Work Package" value={form.work_package || ''} onChange={set('work_package')} style={{ gridColumn: '1/-1' }} />
            </FormGrid>
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── MAIN ─────────────────────────────────────
export default function ProjectSetupPage({ toast }) {
  const { projectId } = useParams();

  if (!projectId) return <NeedProject />;
  return (
    <div className="fade-in">
      <SectionHeader title="Project Divisions" subtitle="Project Divisions" />
      <ProjectDivisionsTab projectId={projectId} toast={toast} />
    </div>
  );
}
